/**
 * Property-ownership verification v1 — shared constants, copy and helpers.
 *
 * Contract: docs/plans/ownership-verification/README.md (API + Frontend).
 * Legal copy: docs/plans/ownership-verification/legal-review.md §3–§5 — the
 * strings below marked VERBATIM must not be paraphrased (needs counsel review
 * before any wording change).
 *
 * Kept separate from `agents/ownershipVerificationAgent.js` so tests can mock
 * the agent without losing these constants.
 */

/** Must equal the server's OWNERSHIP_ATTESTATION_VERSION. */
export const OWNERSHIP_ATTESTATION_VERSION = 'ownership-attestation-v1';

/** "Verified" ≡ status in {auto_approved, approved}. */
export const VERIFIED_STATUSES = ['auto_approved', 'approved'];

/** Statuses that let the seller continue past the Confirm Ownership step. */
export const ADVANCEABLE_STATUSES = ['auto_approved', 'approved', 'needs_review'];

/** Statuses in which the seller may (re)submit documents. */
export const SUBMITTABLE_STATUSES = ['not_started', 'more_info_requested', 'revoked'];

/** Every status the admin queue can filter on (plus 'all'). */
export const ADMIN_STATUS_FILTERS = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'more_info_requested', label: 'More info requested' },
  { value: 'auto_approved', label: 'Auto-approved' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'processing', label: 'Processing' },
  { value: 'all', label: 'All' },
];

export const STATUS_LABELS = {
  not_started: 'Not started',
  processing: 'Processing',
  auto_approved: 'Documents reviewed',
  needs_review: 'Under review',
  more_info_requested: 'More information requested',
  approved: 'Documents reviewed',
  rejected: 'Not approved',
  revoked: 'Documents needed again',
};

/** Admin-facing status labels (distinguish auto vs staff approval). */
export const ADMIN_STATUS_LABELS = {
  ...STATUS_LABELS,
  auto_approved: 'Auto-approved',
  approved: 'Approved',
  rejected: 'Rejected',
  revoked: 'Revoked',
};

export const CAPACITY_OPTIONS = [
  { value: 'sole_owner', label: 'I am the sole owner' },
  { value: 'co_owner', label: 'I am a co-owner (joint tenant, tenant in common, or spouse on title)' },
  { value: 'entity', label: 'I act for a company that owns it (LLC, corporation, partnership)' },
  { value: 'trust', label: 'I am a trustee of a trust that owns it' },
  { value: 'estate', label: "I'm the court-appointed personal representative (executor or administrator) of a deceased owner's estate" },
  { value: 'power_of_attorney', label: 'I am an agent under a power of attorney' },
  { value: 'guardian', label: 'I am a court-appointed guardian or conservator' },
];

export const CAPACITY_LABELS = {
  sole_owner: 'Sole owner',
  co_owner: 'Co-owner',
  entity: 'Entity representative',
  trust: 'Trustee',
  estate: 'Estate representative',
  power_of_attorney: 'Agent under power of attorney',
  guardian: 'Guardian / conservator',
};

/** Capacities that need `capacity_detail` and authority documents. */
export const AUTHORITY_CAPACITIES = ['entity', 'trust', 'estate', 'power_of_attorney', 'guardian'];

export const needsAuthority = (capacity) => AUTHORITY_CAPACITIES.includes(capacity);

/** Prompt + example for the `capacity_detail` field, per capacity. */
export const CAPACITY_DETAIL_HINTS = {
  entity: { label: 'Company name and your role', placeholder: 'Manager of Acme Holdings LLC' },
  trust: { label: 'Trust name and date', placeholder: 'Smith Revocable Trust dated 3/1/2019' },
  estate: { label: 'Estate name and probate case number', placeholder: 'Estate of Jane Doe, Dane County Case No. 2026PR000123' },
  power_of_attorney: { label: 'Owner you act for and date of the power of attorney', placeholder: 'John Doe, power of attorney dated 5/2/2024' },
  guardian: { label: 'Person under guardianship and court case number', placeholder: 'Mary Doe, Dane County Case No. 2024GN000123' },
};

/** What we ask for per authority capacity (legal-review.md §3 "Ask for"). */
export const AUTHORITY_REQUIREMENTS = {
  entity:
    "Upload the company's formation document (articles of organization or incorporation, or certificate of formation) and an operating agreement excerpt, resolution, or written consent that authorizes the sale and names you as someone who can sign for the company.",
  trust:
    'Upload a certification of trust that names you as a current trustee and states the power to sell. You do not need to upload the full trust.',
  estate:
    "Upload your current letters from the probate court (letters testamentary or letters of administration; in Wisconsin, domiciliary letters) and the death certificate. If no court has appointed you (for example, you're using a transfer by affidavit, affidavit of heirship, or muniment of title), upload what you have and a staff member will review it.",
  power_of_attorney:
    'Upload the complete power of attorney, including the pages that give authority over real property. In Texas, a power of attorney used to sell real property must be recorded with the county clerk before closing. If yours has been recorded, upload the recorded copy.',
  guardian:
    "Upload your letters of guardianship or conservatorship. If the court has already approved the sale, upload that order too. If it hasn't, a staff member will review your letters; most sales by a guardian need court approval before closing.",
};

export const PROOF_DOCUMENT_TYPES = [
  { value: 'deed', label: 'Recorded deed' },
  { value: 'tax_bill', label: 'Most recent property tax bill or assessment notice' },
  { value: 'title_policy', label: "Owner's title insurance policy" },
];

export const AUTHORITY_DOCUMENT_TYPES = [
  { value: 'trust_certification', label: 'Certification of trust' },
  { value: 'entity_authority', label: 'Company authority (formation doc, resolution)' },
  { value: 'letters', label: 'Letters (estate, guardianship)' },
  { value: 'power_of_attorney', label: 'Power of attorney' },
  { value: 'court_order', label: 'Court order' },
  { value: 'name_change', label: 'Name change (marriage, divorce, court order)' },
  { value: 'death_certificate', label: 'Death certificate' },
  { value: 'other', label: 'Other' },
];

/** Sensible default authority doc type per capacity. */
export const DEFAULT_AUTHORITY_TYPE = {
  entity: 'entity_authority',
  trust: 'trust_certification',
  estate: 'letters',
  power_of_attorney: 'power_of_attorney',
  guardian: 'letters',
};

const ALL_DOC_TYPE_LABELS = [...PROOF_DOCUMENT_TYPES, ...AUTHORITY_DOCUMENT_TYPES].reduce(
  (acc, o) => ({ ...acc, [o.value]: o.label }),
  {
    mortgage_statement: 'Mortgage statement',
    entity_document: 'Entity document',
    unreadable: 'Unreadable',
  },
);

export const documentTypeLabel = (value) =>
  ALL_DOC_TYPE_LABELS[value] || (value ? String(value).replace(/_/g, ' ') : '—');

// ── File rules (mirror the server: PDF/JPEG/PNG, ≤10 MB each — Gemini's inline
// request cap — 1 proof, 0–5 authority)
export const MAX_FILE_MB = 10;
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
export const MAX_AUTHORITY_FILES = 5;
export const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const ACCEPT_ATTRIBUTE = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

/**
 * Client-side mirror of the server's upload rules. The server still validates
 * magic bytes; this only saves the seller a round-trip.
 *
 * @param {File} file
 * @returns {string|null} a seller-facing error, or null when acceptable.
 */
export function validateOwnershipFile(file) {
  if (!file) return 'Choose a file to upload.';
  const ext = (file.name || '').split('.').pop().toLowerCase();
  const typeOk = file.type
    ? ACCEPTED_MIME_TYPES.includes(file.type)
    : ACCEPTED_EXTENSIONS.includes(ext);
  if (!typeOk) return `${file.name || 'This file'} is not a PDF, JPEG or PNG.`;
  if (file.size > MAX_FILE_BYTES) return `${file.name || 'This file'} is larger than ${MAX_FILE_MB} MB.`;
  if (file.size === 0) return `${file.name || 'This file'} is empty.`;
  return null;
}

export const isVerifiedStatus = (status) => VERIFIED_STATUSES.includes(status);

/**
 * "2026-09" → "September 2026". Returns '' for anything unparseable.
 * @param {string|null|undefined} yyyyMm
 */
export function formatReviewedMonth(yyyyMm) {
  if (typeof yyyyMm !== 'string') return '';
  const m = /^(\d{4})-(\d{2})/.exec(yyyyMm);
  if (!m) return '';
  const month = Number(m[2]);
  if (month < 1 || month > 12) return '';
  const d = new Date(Date.UTC(Number(m[1]), month - 1, 1));
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// ── Admin: machine-readable reasons / red flags → readable labels ─────────
export const REASON_LABELS = {
  name_mismatch: 'Owner name on the document does not match the seller account',
  address_mismatch: 'Property address on the document does not match the listing',
  low_confidence: 'Document was hard to read (low extraction confidence)',
  unsupported_document: 'Document type is not accepted as proof of ownership',
  capacity_requires_review: 'Seller is acting for someone else (trust, company, estate, POA, guardian)',
  authority_documents_submitted: 'Seller uploaded authority documents that need review',
  unrecorded_deed: 'Deed shows no recording stamp or document number',
  undeclared_co_owners: 'Document lists owners the seller did not declare',
  extraction_failed: 'Automated reading failed — review the document manually',
  previously_revoked: 'Seller resubmitted after an admin revoked their earlier approval',
  resubmitted_after_info_request: 'Seller resubmitted after you requested more information',
};

/** Audit-trail event labels (anything else is humanized). */
export const EVENT_LABELS = {
  submitted: 'Submitted',
  extracted: 'Extracted',
  auto_approved: 'Auto-approved',
  flagged: 'Flagged for review',
  approved: 'Approved',
  rejected: 'Rejected',
  info_requested: 'More information requested',
  revoked: 'Revoked',
  reopened: 'Reopened',
  document_viewed: 'Document viewed',
  purged: 'Files purged',
  refund: 'Refund',
};

export const eventLabel = (action) => EVENT_LABELS[action] || humanize(action);

export const RED_FLAG_LABELS = {
  new_account: 'Seller account is less than 7 days old',
  price_far_below_estimate: 'Asking price is far below the AI price estimate',
  out_of_state_owner_mailing: 'Owner mailing address is out of state',
  recent_nominal_quitclaim: 'Recent quitclaim deed (possible nominal-consideration transfer)',
  recent_name_change: 'Seller changed their account name in the last 30 days',
  prior_rejection_at_address: 'Ownership was previously rejected or revoked for this address',
};

const humanize = (code) =>
  String(code || '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

/** Label a reason code, including `red_flag:<name>` reasons. */
export function reasonLabel(code) {
  if (typeof code !== 'string') return '';
  if (code.startsWith('red_flag:')) {
    const flag = code.slice('red_flag:'.length);
    return `Red flag: ${RED_FLAG_LABELS[flag] || humanize(flag)}`;
  }
  return REASON_LABELS[code] || humanize(code);
}

export const redFlagLabel = (code) => RED_FLAG_LABELS[code] || humanize(code);

// ── Legal copy (legal-review.md) — VERBATIM ────────────────────────────────

/** §5 heading. */
export const SELLER_UPLOAD_HEADING = 'Confirm you can sell this property';

/** §5 intro paragraph. */
export const SELLER_UPLOAD_INTRO =
  "Upload one document that shows the current owner — your recorded deed, most recent property tax bill, or owner's title insurance policy. If the property is owned by a trust, company, or estate, or you're acting under a power of attorney or as a guardian or conservator, choose that option and we'll tell you what else to upload.";

/** §5 "How we check it" — split around the bold sentence. */
export const SELLER_HOW_WE_CHECK = {
  lead: 'How we check it:',
  body:
    "Automated tools, including an AI model run by our service provider, read the owner name, address and parcel number and compare them with the details you entered. If they don't match, a cutthe6.com staff member reviews your document, and your listing can't be published until that review is complete. This check helps prevent fraud on our platform.",
  emphasis:
    'It is not a title search and does not confirm that your title is clear or who must sign to sell',
  tail: ' — your title company or attorney handles that before closing.',
};

/** §5 "Protect your information". */
export const SELLER_PROTECT_INFO = {
  lead: 'Protect your information:',
  body:
    "Upload only the pages that show the owner name, address and parcel number. Black out Social Security numbers and bank or loan account numbers. Do not upload a driver's license or other ID.",
};

/** §5 "How we handle your document". */
export const SELLER_DOCUMENT_HANDLING = {
  lead: 'How we handle your document:',
  body:
    'It is stored encrypted, only cutthe6.com staff reviewing it can see it, it is never shown to buyers, and it is not used to train AI models. We delete the file 90 days after our decision, unless we need it longer to investigate suspected fraud or to comply with law. We keep a record of the decision and the details we extracted for 7 years after your listing ends. See our Privacy Notice.',
};

/** §5 attestation checkbox label (unchecked by default). */
export const SELLER_ATTESTATION_TEXT =
  "I confirm that I own this property or am legally authorized to sell it, that everyone whose consent is required has agreed to this listing, and that the document I'm uploading is a genuine, unaltered copy. I understand that a false or altered document can result in removal of my listing and may violate the law.";

/** Power-of-attorney confirmation (legal, 2026-09-29) — second required checkbox. */
export const POA_CONFIRMATION_TEXT =
  'I confirm that this power of attorney has not been revoked and that, to my knowledge, the owner I act for is living.';

/** §4 badge label. */
export const BADGE_LABEL = 'Ownership documents reviewed';

/** §4 tooltip paragraph 1 (the "Reviewed {Month YYYY}." sentence is appended). */
export const BADGE_TOOLTIP_P1 =
  'cutthe6.com reviewed a document the seller provided (such as a recorded deed or property tax bill) and found that it lists the seller, or someone the seller showed authority to act for, as the owner of this property.';

/** §4 tooltip paragraph 2, in segments so the bold runs render bold. */
export const BADGE_TOOLTIP_P2 = {
  emphasis1: 'This is not a title search, title insurance, or a guarantee',
  body1:
    " of who owns this property, that the seller can sell it, or that it is free of liens or other claims. We do not verify the seller's identity. Before you sign a contract or send money, have a title company or real estate attorney confirm ownership. ",
  emphasis2: 'Never wire money based on instructions received by email or text',
  body2: ' — call your title company at a number you look up yourself.',
};

// ── Admin reviewer checklist (legal-review.md §3) ─────────────────────────
export const REVIEWER_GENERAL_GUIDANCE = [
  'Verify against free public records — county register of deeds, county tax roll; WI Statewide Parcel Map (where the county includes owner names); TX county appraisal district (CAD) site. Record what you checked.',
  `Do not decide legal questions — if unclear, request more documents or escalate. Never tell a seller someone "doesn't need to sign."`,
];

export const REVIEWER_CHECKLIST = [
  { situation: 'Co-owners (JT / TIC)', askFor: 'Nothing extra', check: 'All deed owners listed in "other owners"; seller is one of them.' },
  { situation: 'Spouse not on title', askFor: 'Marital status only', check: "WI: homestead conveyance needs both spouses (§706.02(1)(f)). TX: homestead joinder even for separate property (Fam. Code §5.001). Record spouse's name; do not block." },
  { situation: 'LLC / corp / partnership', askFor: 'Formation doc + operating agreement excerpt or resolution naming the signer; good standing', check: 'WI DFI search / TX SOS + Comptroller "active". Signer named/officer.' },
  { situation: 'Revocable trust', askFor: 'Certification of trust (WI §701.1013; TX Prop. Code §114.086)', check: "Trust name matches vesting deed; seller is current trustee; power of sale stated; co-trustee signing noted. Don't request full trust." },
  { situation: 'Estate', askFor: 'Letters (WI Domiciliary Letters; TX Letters Testamentary/Administration) + death certificate', check: 'Current, name the seller. WI Transfer by Affidavit / TX muniment or heirship affidavit → escalate. TX dependent administration → court order.' },
  { situation: 'Power of attorney', askFor: "The POA + agent statement it's unrevoked and principal living", check: 'Real-property authority granted. TX: must be recorded (Est. Code §751.151). Absent elderly principal → heightened review.' },
  { situation: 'Guardian / conservator', askFor: 'Letters + court order authorizing sale', check: 'Court approval usually required.' },
  { situation: 'Name change', askFor: 'Marriage cert / divorce decree / court order', check: 'Divorce: check which spouse was awarded the property.' },
  { situation: 'Deceased joint tenant', askFor: 'Death certificate + recorded termination (WI HT-110)', check: 'TX: survivorship only if written in the deed — otherwise treat as an estate.' },
  { situation: 'TOD-deed beneficiary', askFor: 'Death cert + recorded TOD instrument/affidavit', check: '—' },
  { situation: 'Formatting-only differences (initials, suffixes, diacritics, name order)', askFor: 'Nothing', check: 'Approve.' },
];

export const REVIEWER_RED_FLAGS =
  'Red flags that force manual review even on a match: vacant land / no mortgage of record · owner mailing address out of state or overseas · account < 7 days old · price well below the AI estimate · recent quitclaim for nominal consideration · seller refuses phone/video · requests a fast cash close.';
